import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

import {
  useTreatmentServiceGetEpidemiologyCaseListEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpoint,
  useTreatmentServiceGetTreatmentPlanListEndpoint
} from 'src/api/queries'
import {
  Gender,
  ListSortDirection,
  TranslationDto,
  TreatmentPlanSortBy,
  TreatmentPlanStatus
} from 'src/api/requests'
import { Text } from 'src/components'
import { TreatmentStatusTag } from 'src/features/persons/components/PersonTreatmentsSection/components/TreatmentStatusTag'
import { useLatestConsultationDiagnoses } from 'src/features/persons/hooks/useLatestConsultationDiagnoses'
import { getFullName } from 'src/features/users/utils'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { usePatientBreadcrumb } from './usePatientBreadcrumb'

export const usePatientInformation = (personId?: string) => {
  const { t, currentLang } = useLocales()

  const { person } = usePatientBreadcrumb(personId)

  const { data: epidemiologyCaseData } =
    useTreatmentServiceGetEpidemiologyCaseListEndpoint({
      pageNumber: 1,
      pageSize: 5,
      patientId: personId
    })

  // The green card shows the newest final diagnosis, even when the latest
  // visit recorded none.
  const { final: finalDiagnosis } = useLatestConsultationDiagnoses(personId)

  const { data: latestTreatmentList } =
    useTreatmentServiceGetTreatmentPlanListEndpoint(
      {
        personId,
        pageNumber: 1,
        pageSize: 1,
        sortBy: TreatmentPlanSortBy.START_DATE,
        sortDirection: ListSortDirection.DESCENDING
      },
      [String(personId)],
      { enabled: !!personId }
    )

  const latestTreatmentId = latestTreatmentList?.data?.[0]?.id

  const { data: treatmentPlan } = useTreatmentServiceGetTreatmentPlanEndpoint(
    { id: String(latestTreatmentId) },
    [String(latestTreatmentId)],
    { enabled: !!latestTreatmentId }
  )

  const patientInformationInfo = [
    {
      key: 'fullName',
      label: t('form.first_name') + ' ' + t('form.last_name'),
      children: getFullName(person?.lastName, person?.firstName)
    },
    {
      key: 'testrapCode',
      label: t('common.testrap_code'),
      children: person?.testrapCode ?? '-'
    },
    {
      key: 'gender',
      label: t('form.gender'),
      children:
        person?.gender === Gender.MALE ? t('form.male') : t('form.female')
    },
    {
      key: 'birthDate',
      label: t('form.birth_date'),
      children: person?.birthDate
        ? dayjs(person?.birthDate).format(dayMonthYearFormatWithDot)
        : '-'
    },
    {
      key: 'address',
      label: t('form.address'),
      children: person?.currentAddress?.cuatmLocality
        ? [
            person?.currentAddress?.cuatmLocality?.translation?.[
              currentLang?.value as keyof TranslationDto
            ],
            person?.currentAddress?.street,
            person?.currentAddress?.streetNumber,
            person?.currentAddress?.apartmentNumber
          ]?.join(', ')
        : '-'
    },
    {
      key: 'simeCode',
      label: t('common.sime_code'),
      children: person?.simeCode ?? '-'
    },
    {
      key: 'tarvCode',
      label: t('common.tarv_code'),
      children: person?.tarvCode ?? '-'
    },
    {
      key: 'institution',
      label: t('common.institution'),
      children: person?.institution?.name ?? '-'
    },
    {
      key: 'epidemiologyCase',
      label: t('epidemiology.epidemiology_number'),
      children: (() => {
        const codes =
          epidemiologyCaseData?.data?.map((item) => item?.code) || []

        const maxVisible = 3

        if (codes.length === 0) return '-'

        if (codes.length > maxVisible) {
          const displayed = codes.slice(0, maxVisible).join(', ')

          return (
            <>
              <Text>{displayed}</Text>
              <Text>... </Text>
              <Link to={paths.patients.epidemiologyCases.root()}>
                {t('common.view_more')}
              </Link>
            </>
          )
        }

        return codes.join(', ')
      })()
    }
  ]

  const patientDiagnosticInfo = [
    {
      key: 'diagnostic',
      label: t('consultations.final_diagnostic'),
      children: finalDiagnosis?.finalDiagnostics
        ?.map((item) => {
          const name =
            item.translation?.[currentLang?.value as keyof TranslationDto]

          return item.code ? `${item.code} – ${name}` : name
        })
        ?.join(', ')
    },
    {
      key: 'consultationDate',
      label: t('consultations.consultation_date'),
      children: finalDiagnosis?.consultationDate
        ? dayjs(finalDiagnosis.consultationDate).format(
            dayMonthYearFormatWithDot
          )
        : '-'
    }
  ]

  const patientTreatmentInfo = [
    {
      key: 'treatmentType',
      label: t('treatments.treatment_type'),
      children:
        treatmentPlan?.treatmentType?.translation?.[
          currentLang?.value as keyof TranslationDto
        ] ?? '-'
    },
    {
      key: 'regimen',
      label: t('treatments.regiment_type'),
      children:
        treatmentPlan?.treatmentTypeRegimen?.translation?.[
          currentLang?.value as keyof TranslationDto
        ] ?? '-'
    },
    {
      key: 'legalEntity',
      label: t('common.institution'),
      children: treatmentPlan?.legalEntity?.name ?? '-'
    },
    {
      key: 'prescribedBy',
      label: t('treatments.prescribed_by'),
      children:
        getFullName(
          treatmentPlan?.prescribedBy?.firstName,
          treatmentPlan?.prescribedBy?.lastName
        ) || '-'
    },
    {
      key: 'startDate',
      label: t('treatments.treatment_start_date'),
      children: treatmentPlan?.startDate
        ? dayjs(treatmentPlan.startDate).format(dayMonthYearFormatWithDot)
        : '-'
    },
    {
      key: 'endDate',
      label: t('treatments.treatment_end_date'),
      children: treatmentPlan?.endDate
        ? dayjs(treatmentPlan.endDate).format(dayMonthYearFormatWithDot)
        : '-'
    },
    {
      key: 'status',
      label: t('analysis.status'),
      children: <TreatmentStatusTag status={treatmentPlan?.status} />
    }
  ]

  const hasActiveTreatment =
    treatmentPlan?.status === TreatmentPlanStatus.IN_PROGRESS ||
    treatmentPlan?.status === TreatmentPlanStatus.NEW

  return {
    patientInformationInfo,
    patientDiagnosticInfo: finalDiagnosis ? patientDiagnosticInfo : [],
    patientTreatmentInfo: hasActiveTreatment ? patientTreatmentInfo : []
  }
}
